import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { CATEGORY_META } from "@/lib/constants";
import { Clock, X } from "lucide-react";

const fmt = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const presets = () => {
  const now = new Date();
  const day = now.getDay();
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - ((day + 6) % 7)); // Monday
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    week: { from: iso(weekStart), to: iso(now) },
    month: { from: iso(monthStart), to: iso(now) },
  };
};

export default function TimeReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const r = await api.get("/time/report", { params });
      setData(r.data);
    } catch (e) {
      toast.error(apiErr(e, "Could not load the time report"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => {
    const p = presets()[key];
    setFrom(p.from); setTo(p.to);
  };
  const clear = () => { setFrom(""); setTo(""); };
  const hasRange = from || to;

  return (
    <div className="p-6 lg:p-10 max-w-[1000px]" data-testid="time-report-page">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Clock className="h-6 w-6 text-zinc-900" />
          <h1 className="font-heading text-4xl font-black tracking-tighter">Time Report</h1>
        </div>
        <p className="text-zinc-500 text-sm mt-2">Tracked hours by project, team member and task.</p>
      </header>

      {/* Date range filter */}
      <div className="bg-white border border-zinc-200 p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="time-report-filter">
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">From</label>
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)}
            className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm" data-testid="time-report-from" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">To</label>
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)}
            className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm" data-testid="time-report-to" />
        </div>
        <div className="h-5 w-px bg-zinc-200 mx-1" />
        <button onClick={() => applyPreset("week")} className="text-xs font-semibold px-2.5 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors" data-testid="time-preset-week">This week</button>
        <button onClick={() => applyPreset("month")} className="text-xs font-semibold px-2.5 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors" data-testid="time-preset-month">This month</button>
        {hasRange && (
          <button onClick={clear} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 ml-1" data-testid="time-clear-range">
            <X className="h-4 w-4" /> All time
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : !data || data.projects.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center"><p className="text-zinc-500 text-sm">{hasRange ? "No time tracked in this date range." : "No time tracked yet."}</p></div>
      ) : (
        <>
          <div className="bg-zinc-900 text-white rounded-sm px-5 py-4 mb-6 flex items-center justify-between" data-testid="time-grand-total">
            <span className="text-sm uppercase tracking-wider text-zinc-400">
              Total tracked{hasRange ? ` · ${from || "start"} → ${to || "now"}` : ""}
            </span>
            <span className="text-2xl font-black">{fmt(data.total_seconds)}</span>
          </div>
          <div className="space-y-4">
            {data.projects.map((p) => (
              <div key={p.project_id} className="border border-zinc-200 bg-white" data-testid={`report-project-${p.project_id}`}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                  <div>
                    <p className="font-heading font-bold">{p.project_name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{(CATEGORY_META[p.category] || {}).label || p.category}</p>
                  </div>
                  <span className="text-lg font-black" data-testid={`report-project-total-${p.project_id}`}>{fmt(p.total_seconds)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 px-5 py-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">By member</p>
                    <div className="space-y-1.5">
                      {p.by_member.map((m) => (
                        <div key={m.user_id} className="flex items-center gap-2 text-sm">
                          <Avatar id={m.user_id} name={m.name} size={22} />
                          <span className="truncate">{m.name}</span>
                          <span className="ml-auto font-semibold text-zinc-700">{fmt(m.seconds)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">By task</p>
                    <div className="space-y-1.5">
                      {p.by_task.map((t) => (
                        <div key={t.task_id} className="flex items-center gap-2 text-sm">
                          <span className="truncate">{t.name}</span>
                          <span className="ml-auto font-semibold text-zinc-700">{fmt(t.seconds)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
